import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import storyImagePlanService from '../service/story-image-plan.service';
import { StoryImagePlanEntity, TtsSpeaker } from '../types/entities';

export type SceneAdvancedSettingsProps = {
  planId: string;
};

const labelOrDash = (v?: string | null) => (v ? v : '—');

const speakerLabel = (s?: TtsSpeaker | null) => (s ? s : '—');

const EffectOptionsView: React.FC<{ opts?: StoryImagePlanEntity['effectOptions'] | null }> = ({ opts }) => {
  if (!opts || (!opts.amount && !opts.segmentMs && !opts.fillMode)) return null;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      {typeof opts.amount === 'number' && (
        <Chip size="small" label={`amount: ${opts.amount}`} />
      )}
      {typeof opts.segmentMs === 'number' && (
        <Chip size="small" label={`segment: ${opts.segmentMs}ms`} />
      )}
      {opts.fillMode && (
        <Chip size="small" label={`fillMode: ${opts.fillMode}`} />
      )}
    </Stack>
  );
};

const SceneAdvancedSettings: React.FC<SceneAdvancedSettingsProps> = ({ planId }) => {
  const [plan, setPlan] = React.useState<StoryImagePlanEntity | null>(null);

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      const p = await storyImagePlanService.getPlanById(planId);
      if (!alive) return;
      setPlan(p);
    };
    load();
    return () => { alive = false; };
  }, [planId]);

  return (
    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'rgba(0,0,0,0.02)' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        Özel Ayarlar
      </Typography>
      <Stack spacing={1}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Chip size="small" label={`Motion: ${labelOrDash(plan?.effectPreset)}`} />
          <Chip size="small" label={`Subtitles: ${labelOrDash(plan?.subtitlePreset)}`} />
          <Chip size="small" label={`Waveform: ${labelOrDash(plan?.waveformPreset)}`} />
          <Chip size="small" label={`Dominant Speaker: ${speakerLabel(plan?.dominantSpeaker as any)}`} />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Chip size="small" label={`Transition: ${labelOrDash(plan?.transitionType as any)}`} />
          {typeof plan?.transitionDurationMs === 'number' && (
            <Chip size="small" label={`Transition Dur: ${plan.transitionDurationMs}ms`} />
          )}
        </Stack>
        <EffectOptionsView opts={plan?.effectOptions} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {plan?.subtitleColor && (
            <Chip size="small" label={`Subtitle Color: ${plan.subtitleColor}`} />
          )}
          {typeof plan?.subtitleFontSize === 'number' && (
            <Chip size="small" label={`Subtitle Size: ${plan.subtitleFontSize}px`} />
          )}
          {plan?.subtitleBgColor && (
            <Chip size="small" label={`Subtitle BG: ${plan.subtitleBgColor}`} />
          )}
        </Stack>
        {typeof plan?.gapAfterAudioMs === 'number' && (
          <Chip size="small" label={`Gap after audio: ${plan.gapAfterAudioMs}ms`} />
        )}
      </Stack>
    </Paper>
  );
};

export default SceneAdvancedSettings;
